import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  MenuToggle,
  Select,
  SelectList,
  SelectOption,
  Spinner,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { ChartBarIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, ThProps, Thead, Tr } from '@patternfly/react-table';
import { useAdminVerdaAvailabilityHistory } from '../../../hooks/useAdminVerdaAvailabilityHistory';
import type { VerdaHistoryInstanceType } from '../../../types/models/Verda';
import { formatCurrencyAmount } from '../../../utils/money';
import { VerdaErrorAlert, VerdaRefreshButton } from './VerdaTabParts';
import VerdaAvailabilityShapeDetail from './VerdaAvailabilityShapeDetail';
import {
  MARKET_LABELS,
  type VerdaMarket,
  type VerdaShapeScope,
  type VerdaShare,
  type VerdaWindowPreset,
  WINDOW_PRESETS,
  formatShare,
  formatUtc,
  inScope,
  presetQuery,
  rankSoldOutEverywhere,
  shapeLabel,
  soldOutShare,
} from './verdaAvailabilityHistory';

interface VerdaAvailabilityHistoryTabProps {
  /** Defers the fetch until the staff gate has passed. */
  isStaff: boolean;
}

type SortKey = 'shape' | 'price' | 'everywhere';
const SORT_COLUMN_INDEX: Record<SortKey, number> = { shape: 0, price: 1, everywhere: 2 };
const SUBTLE = 'pf-v6-u-font-size-sm pf-v6-u-text-color-subtle';

/** A share for sorting: the never-deployable and unsampled rows sink below every real share. */
const shareRank = (share: VerdaShare): number =>
  share.kind === 'share' ? share.value : share.kind === 'never' ? -1 : -2;

const ShareCell: React.FunctionComponent<{ share: VerdaShare }> = ({ share }) => {
  if (share.kind === 'share') {
    return <span title={`sold out in ${share.soldOut} of ${share.samples} samples`}>{formatShare(share.value)}</span>;
  }
  if (share.kind === 'never') {
    return (
      <span className={SUBTLE} title="Never deployable in this window: not offered there, or sold out throughout">
        never deployable
      </span>
    );
  }
  return <span className={SUBTLE}>—</span>;
};

const StatTile: React.FunctionComponent<{ label: string; value: string; detail?: string }> = ({
  label,
  value,
  detail,
}) => (
  <Card isCompact isPlain className="verda-history-stat">
    <CardBody>
      <div className={SUBTLE}>{label}</div>
      <div className="verda-history-stat__value">{value}</div>
      {detail && <div className={SUBTLE}>{detail}</div>}
    </CardBody>
  </Card>
);

/**
 * Admin › Verda › Availability history — the business-plan report over the capacity series the Verda
 * availability sampler records every 5 minutes (`GET /admin/verda/availability-history`, a database read):
 * how often each GPU shape could not be deployed anywhere, per location, and when. One filter row scopes
 * everything below it: the window (whole UTC days, ≤ 31), the market (on-demand / spot) and the shapes
 * (single-GPU — the best "pool saturated" signal — or all). A bar or a table row opens that shape's
 * detail (timeline, hour × weekday heatmap, daily share).
 *
 * The honest-reading rules (service/Verda/CLAUDE.md) are on the page itself: a share is of SAMPLES, gaps
 * are no data, and a never-deployable combination is shown apart, never as a 100% bar.
 */
const VerdaAvailabilityHistoryTab: React.FunctionComponent<VerdaAvailabilityHistoryTabProps> = ({ isStaff }) => {
  const [preset, setPreset] = useState<VerdaWindowPreset>(7);
  const [isPresetOpen, setIsPresetOpen] = useState(false);
  const [market, setMarket] = useState<VerdaMarket>('on_demand');
  const [scope, setScope] = useState<VerdaShapeScope>('single-gpu');
  const [selected, setSelected] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('everywhere');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // "Today" is the backend's (UTC): the default 7-day read resolves it; longer presets end on it.
  const [anchorTo, setAnchorTo] = useState<string | null>(null);

  // 7 days is the backend's default window (no query: never a second read once the anchor is known).
  const query = preset === 7 ? null : presetQuery(anchorTo ?? new Date().toISOString().slice(0, 10), preset);
  const { history, loading, error, refetch } = useAdminVerdaAvailabilityHistory(query, isStaff);

  useEffect(() => {
    if (history !== null && (anchorTo === null || history.window.to > anchorTo)) {
      setAnchorTo(history.window.to);
    }
  }, [history, anchorTo]);

  const types = useMemo(() => history?.instance_types ?? [], [history]);
  const { ranked, never } = useMemo(() => rankSoldOutEverywhere(types, market, scope), [types, market, scope]);
  const rows = useMemo(() => {
    const inView = types.filter((t) => inScope(t, scope));
    const price = (t: VerdaHistoryInstanceType) =>
      Number((market === 'on_demand' ? t.on_demand_per_hour : t.spot_per_hour) ?? Number.NaN);
    const compare: Record<SortKey, (a: VerdaHistoryInstanceType, b: VerdaHistoryInstanceType) => number> = {
      shape: (a, b) =>
        shapeLabel(a).localeCompare(shapeLabel(b), 'en') || a.instance_type.localeCompare(b.instance_type),
      price: (a, b) => (Number.isNaN(price(a)) ? -1 : price(a)) - (Number.isNaN(price(b)) ? -1 : price(b)),
      everywhere: (a, b) => shareRank(soldOutShare(a.anywhere, market)) - shareRank(soldOutShare(b.anywhere, market)),
    };
    const sign = sortDirection === 'asc' ? 1 : -1;
    return [...inView].sort((a, b) => sign * compare[sortKey](a, b) || compare.shape(a, b));
  }, [types, scope, market, sortKey, sortDirection]);

  const sortParams = (key: SortKey): ThProps['sort'] => ({
    sortBy: { index: SORT_COLUMN_INDEX[sortKey], direction: sortDirection, defaultDirection: 'desc' },
    onSort: (_event, _index, direction) => {
      setSortKey(key);
      setSortDirection(direction);
    },
    columnIndex: SORT_COLUMN_INDEX[key],
  });

  const select = (code: string) => setSelected((current) => (current === code ? null : code));
  const marketLabel = MARKET_LABELS[market].toLowerCase();

  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="availability history" message={error} />;
    }
    if (history === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the Verda availability history" />
        </Bullseye>
      );
    }
    const { coverage, window } = history;
    if (coverage.samples === 0) {
      return (
        <EmptyState titleText="No sample in this window" icon={ChartBarIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            The sampler records Verda&apos;s capacity every 5 minutes once it is armed (production only). Pick a longer
            window, or come back once samples exist.
          </EmptyStateBody>
        </EmptyState>
      );
    }
    return (
      <div style={{ opacity: loading ? 0.5 : 1 }}>
        <Flex className="pf-v6-u-mb-md" spaceItems={{ default: 'spaceItemsLg' }}>
          <FlexItem>
            <StatTile
              label="Coverage"
              value={formatShare(coverage.samples / Math.max(1, coverage.expected))}
              detail={`${coverage.samples.toLocaleString('en')} of ${coverage.expected.toLocaleString('en')} five-minute slots sampled`}
            />
          </FlexItem>
          <FlexItem>
            <StatTile
              label="Sampled from"
              value={coverage.first_sample_at ? formatUtc(coverage.first_sample_at) : '—'}
              detail={coverage.last_sample_at ? `to ${formatUtc(coverage.last_sample_at)}` : undefined}
            />
          </FlexItem>
          <FlexItem>
            <StatTile
              label="Shapes in view"
              value={String(rows.length)}
              detail={`of ${types.length} recorded · ${history.locations.join(', ')}`}
            />
          </FlexItem>
        </Flex>
        {coverage.samples < coverage.expected && (
          <Content component="p" className={`${SUBTLE} pf-v6-u-mb-md`}>
            {(coverage.expected - coverage.samples).toLocaleString('en')} slots of this window have no sample (the
            sampler or Verda was down, or sampling had not started yet). They are left out of every share — never
            counted as sold out.
          </Content>
        )}

        <Card>
          <CardTitle>
            Sold out everywhere — {marketLabel}
            <div className={SUBTLE}>
              Share of samples in which no location could deploy one more instance, {window.from} to {window.to} (UTC).
              {scope === 'single-gpu' && ' Single-GPU shapes: the best sign of a saturated pool.'}
            </div>
          </CardTitle>
          <CardBody>
            {ranked.length === 0 ? (
              <Content component="p">No shape in view was deployable {marketLabel} in this window.</Content>
            ) : (
              <ol className="verda-history-bars" aria-label={`Share of samples sold out everywhere, ${marketLabel}`}>
                {ranked.map(({ type, share }) => (
                  <li key={type.instance_type}>
                    <button
                      type="button"
                      className="verda-history-bar"
                      aria-pressed={selected === type.instance_type}
                      aria-label={`${shapeLabel(type)} (${type.instance_type}): sold out everywhere in ${share.soldOut} of ${share.samples} samples, ${formatShare(share.value)}`}
                      title={`sold out everywhere in ${share.soldOut} of ${share.samples} samples`}
                      onClick={() => select(type.instance_type)}
                    >
                      <span className="verda-history-bar__label">
                        {shapeLabel(type)} <span className={SUBTLE}>{type.instance_type}</span>
                      </span>
                      <span className="verda-history-bar__track">
                        <span className="verda-history-bar__fill" style={{ width: `${share.value * 100}%` }} />
                      </span>
                      <span className="verda-history-bar__value">{formatShare(share.value)}</span>
                    </button>
                  </li>
                ))}
              </ol>
            )}
            {never.length > 0 && (
              <Content component="p" className={`${SUBTLE} pf-v6-u-mt-md`}>
                Never deployable {marketLabel} anywhere in this window — not offered, or sold out throughout (the data
                cannot tell them apart): {never.map((t) => `${shapeLabel(t)} (${t.instance_type})`).join(', ')}.
              </Content>
            )}
          </CardBody>
        </Card>

        {selected !== null && (
          <VerdaAvailabilityShapeDetail
            key={selected}
            instanceType={selected}
            query={query}
            market={market}
            isStaff={isStaff}
            onClose={() => setSelected(null)}
          />
        )}

        <Table aria-label="Sold-out share per location" variant="compact" className="pf-v6-u-mt-lg">
          <Thead>
            <Tr>
              <Th sort={sortParams('shape')}>Shape</Th>
              <Th sort={sortParams('price')} modifier="wrap">
                {MARKET_LABELS[market]} / h
              </Th>
              <Th sort={sortParams('everywhere')} modifier="wrap">
                Sold out everywhere
              </Th>
              {history.locations.map((location) => (
                <Th key={location} modifier="nowrap">
                  {location}
                </Th>
              ))}
              <Th modifier="nowrap">Samples</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((type) => {
              const price = market === 'on_demand' ? type.on_demand_per_hour : type.spot_per_hour;
              const byLocation = new Map(type.locations.map((l) => [l.location_code, l]));
              return (
                <Tr key={type.instance_type} isRowSelected={selected === type.instance_type}>
                  <Td dataLabel="Shape">
                    <Button
                      variant="link"
                      isInline
                      aria-expanded={selected === type.instance_type}
                      onClick={() => select(type.instance_type)}
                    >
                      {shapeLabel(type)}
                    </Button>{' '}
                    <span className={SUBTLE}>{type.instance_type}</span>
                  </Td>
                  <Td dataLabel={`${MARKET_LABELS[market]} / h`} modifier="nowrap">
                    {price === null ? '—' : formatCurrencyAmount(price, 'EUR')}
                  </Td>
                  <Td dataLabel="Sold out everywhere">
                    <ShareCell share={soldOutShare(type.anywhere, market)} />
                  </Td>
                  {history.locations.map((location) => {
                    const counts = byLocation.get(location);
                    return (
                      <Td key={location} dataLabel={location}>
                        <ShareCell share={counts ? soldOutShare(counts, market) : { kind: 'no-data' }} />
                      </Td>
                    );
                  })}
                  <Td dataLabel="Samples">{type.anywhere.samples.toLocaleString('en')}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </div>
    );
  };

  return (
    <div className="verda-history">
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <Select
              isOpen={isPresetOpen}
              selected={preset}
              onSelect={(_event, value) => {
                if (typeof value === 'number' && (WINDOW_PRESETS as readonly number[]).includes(value)) {
                  setPreset(value as VerdaWindowPreset);
                }
                setIsPresetOpen(false);
              }}
              onOpenChange={(open) => setIsPresetOpen(open)}
              toggle={(toggleRef) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsPresetOpen(!isPresetOpen)}
                  isExpanded={isPresetOpen}
                  aria-label="Window"
                >
                  Last {preset} days (UTC)
                </MenuToggle>
              )}
            >
              <SelectList>
                {WINDOW_PRESETS.map((days) => (
                  <SelectOption key={days} value={days}>
                    Last {days} days
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <ToggleGroup aria-label="Market">
              {(['on_demand', 'spot'] as const).map((m) => (
                <ToggleGroupItem
                  key={m}
                  text={MARKET_LABELS[m]}
                  isSelected={market === m}
                  onChange={() => setMarket(m)}
                />
              ))}
            </ToggleGroup>
          </ToolbarItem>
          <ToolbarItem>
            <ToggleGroup aria-label="Shapes">
              <ToggleGroupItem
                text="Single-GPU shapes"
                isSelected={scope === 'single-gpu'}
                onChange={() => setScope('single-gpu')}
              />
              <ToggleGroupItem text="All shapes" isSelected={scope === 'all'} onChange={() => setScope('all')} />
            </ToggleGroup>
          </ToolbarItem>
          <ToolbarItem>
            <VerdaRefreshButton loading={loading} onRefresh={refetch} what="availability history" />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>
      <Alert variant="info" isInline isPlain component="h2" title="How to read these figures" className="pf-v6-u-mb-md">
        Every 5 minutes the sampler records whether Verda could deploy one more instance of each shape, per location. A
        sold-out share is a share of those samples: a lower bound on saturation, not a utilisation rate. Multi-GPU
        shapes run out first. A shape never deployable in the window may simply not be offered there. Days and hours are
        UTC.
      </Alert>
      {renderBody()}
    </div>
  );
};

export default VerdaAvailabilityHistoryTab;
